// Copyright ©️ 2026 eVolve MEP, LLC

import { useMemo } from 'react';

import { type AssignableRole, useEvolvePost } from '@evolvemep/api-ts';
import { Paper, Space, Table, Text, TextInput, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';

import { useFormModal } from 'components/Mantine/Modals/FormModal';
import { WrappedSelect } from 'components/Mantine/TypeSafeSelect/WrappedSelect';
import type { WithRequiredNotNull } from 'helpers/helper-types';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { strongValues } from 'helpers/strongEntries';

import { ManageRevitRolesNotes } from './ManageRevitRolesNotes';
import { ManageRevitRolesOptIn } from './ManageRevitRolesOptIn';
import {
  permissionOptions,
  type NameableRole,
  type RoleAndPermissionModalContentsProps,
  type RoleNameFormEntries,
} from './types';

const normalizePermissionLevel = (role: AssignableRole | undefined): AssignableRole | undefined => {
  if (!role) return undefined;
  return {
    ...role,
    permissionLevel: role.permissionLevel ?? 'Full',
    isOriginalNull: isNil(role.permissionLevel),
  };
};

export const ManageRevitRoles = ({
  roles,
  company,
  refetch,
  hasElectrical,
  hasMechanical,
}: WithRequiredNotNull<RoleAndPermissionModalContentsProps, 'company'>) => {
  const { apiCall: setDisplayNames } = useEvolvePost('admin/role/customConfiguration/set');

  const { apiCall: optInToCustomRolePermissionsCall } = useEvolvePost('admin/company/optInToCustomRolePermissions');

  const eEeMEditableRoles = useMemo(
    () => [...new Set(roles.filter((r) => r.isDisplayNameEditable).map((r) => r.defaultDisplayName))],
    [roles],
  );

  const form = useForm<RoleNameFormEntries>({
    initialValues: {
      optInToCustomRolePermissions: company.optInToCustomRolePermissions,
      roles: eEeMEditableRoles.reduce(
        (o, key) => ({
          ...o,
          [key]: {
            eERole: normalizePermissionLevel(roles.find((r) => r.scope === 'EE' && r.defaultDisplayName === key)),
            eMRole: normalizePermissionLevel(roles.find((r) => r.scope === 'EM' && r.defaultDisplayName === key)),
          },
        }),
        {},
      ),
    },
  });

  const rowData = useMemo<NameableRole[]>(
    () =>
      // sort ensures that user roles will always be displayed 1 - 5
      eEeMEditableRoles
        .toSorted((a, b) => a.localeCompare(b))
        .map((dn) => ({
          defaultDisplayName: dn,
          eERole: roles.find((r) => r.scope === 'EE' && r.defaultDisplayName === dn),
          eMRole: roles.find((r) => r.scope === 'EM' && r.defaultDisplayName === dn),
        })),
    [eEeMEditableRoles, roles],
  );

  const onSubmit = async ({ optInToCustomRolePermissions, roles: updatedRoles }: typeof form.values) => {
    await setDisplayNames({
      customDisplayNames: strongValues(updatedRoles).flatMap(({ eERole, eMRole }) => {
        const mEntry = isNotNil(eMRole)
          ? [
              {
                roleId: eMRole.roleId,
                displayName: eMRole.displayName,
                permissionLevel:
                  eMRole.permissionLevel === 'Full' && eMRole.isOriginalNull ? null : eMRole.permissionLevel,
              },
            ]
          : [];
        const eEntry = isNotNil(eERole)
          ? [
              {
                roleId: eERole.roleId,
                displayName: eERole.displayName,
                permissionLevel:
                  eERole.permissionLevel === 'Full' && eERole.isOriginalNull ? null : eERole.permissionLevel,
              },
            ]
          : [];
        return [...mEntry, ...eEntry];
      }),
    }).then(async () => {
      // If custom permissions is nil, then it has not been enabled for this company, and we should not send this request
      if (isNotNil(optInToCustomRolePermissions)) {
        await optInToCustomRolePermissionsCall({ optIn: optInToCustomRolePermissions });
      }
      notifications.show({
        title: 'Successfully updated',
        message: 'Role and Permissions settings updated successfully.',
        color: 'green',
      });
      refetch();
    });
  };

  useFormModal(form, onSubmit);

  return (
    <>
      <ManageRevitRolesOptIn
        optIn={form.values.optInToCustomRolePermissions ?? null}
        setOptIn={(v) => form.setFieldValue('optInToCustomRolePermissions', v)}
      />
      <ManageRevitRolesNotes />
      {hasElectrical && (
        <>
          <Title mt="md" mb="xs" size="md">
            Electrical
          </Title>
          <Paper withBorder radius="md" style={{ overflow: 'hidden' }}>
            <Table>
              <Table.Thead bg="gray.1" __size="xs">
                <Table.Tr>
                  <Table.Th w={200}>User Roles</Table.Th>
                  <Table.Th w={300}>Display Name</Table.Th>
                  <Table.Th w={170}>Project Config Permissions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rowData.map((r) => (
                  <Table.Tr key={r.defaultDisplayName} flex={1}>
                    <Table.Td>
                      <Text>{r.defaultDisplayName}</Text>
                    </Table.Td>
                    <Table.Td>
                      <TextInput
                        mt={1}
                        mb={1}
                        w="100%"
                        maxLength={64}
                        {...form.getInputProps(`roles.${r.defaultDisplayName}.eERole.displayName`)}
                      />
                    </Table.Td>
                    <Table.Td>
                      <WrappedSelect
                        mt={1}
                        mb={1}
                        {...form.getInputProps(`roles.${r.defaultDisplayName}.eERole.permissionLevel`)}
                        w="100%"
                        data={permissionOptions}
                      />
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
        </>
      )}
      {hasMechanical && (
        <>
          <Space mt="xs" />
          <Title size="md" mb="xs">
            Mechanical
          </Title>
          <Paper withBorder radius="md" style={{ overflow: 'hidden' }}>
            <Table>
              <Table.Thead bg="gray.1" __size="xs">
                <Table.Tr m="lg">
                  <Table.Th w={200}>User Roles</Table.Th>
                  <Table.Th w={300}>Display Name</Table.Th>
                  <Table.Th w={170}>Project Config Permissions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rowData.map((r) => (
                  <Table.Tr key={r.defaultDisplayName} flex={1}>
                    <Table.Td>
                      <Text>{r.defaultDisplayName}</Text>
                    </Table.Td>
                    <Table.Td>
                      <TextInput
                        w="100%"
                        mt={1}
                        mb={1}
                        maxLength={64}
                        {...form.getInputProps(`roles.${r.defaultDisplayName}.eMRole.displayName`)}
                      />
                    </Table.Td>
                    <Table.Td>
                      <WrappedSelect
                        mt={1}
                        mb={1}
                        {...form.getInputProps(`roles.${r.defaultDisplayName}.eMRole.permissionLevel`)}
                        w="100%"
                        data={permissionOptions}
                      />
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
        </>
      )}
    </>
  );
};
